import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const parseFiltersFromUrl = <T extends object>(
  search: string,
  defaultFilters: T,
): T => {
  const queryParams = new URLSearchParams(search);
  const parsedFilters = { ...defaultFilters };

  for (const [key, defaultValue] of Object.entries(defaultFilters)) {
    const values = queryParams.getAll(key);
    if (values.length === 0) continue;

    if (Array.isArray(defaultValue)) {
      const firstValue = values[0];
      const parsedNumber = Number(firstValue);

      if (
        !isNaN(parsedNumber) &&
        firstValue !== '' &&
        firstValue !== 'true' &&
        firstValue !== 'false'
      ) {
        (parsedFilters[key as keyof T] as number[]) = values.map(Number);
      } else {
        (parsedFilters[key as keyof T] as unknown as string[]) = values;
      }
    } else if (typeof defaultValue === 'boolean') {
      (parsedFilters[key as keyof T] as boolean) = values[0] === 'true';
    } else if (defaultValue === null) {
      const parsed = Number(values[0]);
      (parsedFilters[key as keyof T] as unknown) =
        !isNaN(parsed) && values[0] !== '' ? parsed : values[0];
    } else {
      (parsedFilters[key as keyof T] as string) = values[0];
    }
  }

  return parsedFilters;
};

const buildSearchParams = (filters: object): URLSearchParams => {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      if (value.length > 0) {
        value.forEach(val => params.append(key, val.toString()));
      }
    } else if (value !== null && value !== undefined && value !== '') {
      params.set(key, value.toString());
    }
  });

  return params;
};

export const useFilterUrlSync = <T extends object>(
  defaultFilters: T,
): [T, Dispatch<SetStateAction<T>>] => {
  const location = useLocation();
  const navigate = useNavigate();

  const [filters, setFilters] = useState<T>(() =>
    parseFiltersFromUrl(location.search, defaultFilters),
  );

  const lastSyncedUrlSearch = useRef<string>(
    new URLSearchParams(location.search).toString(),
  );
  const lastSyncedFilters = useRef<T>(filters);
  const isInitialRender = useRef(true);

  // Initial sync: if URL is empty but defaults have values, sync to URL
  useEffect(() => {
    if (!location.search) {
      const params = buildSearchParams(defaultFilters);
      const searchString = params.toString();

      if (searchString) {
        const newUrl = `${location.pathname}?${searchString}`;
        lastSyncedUrlSearch.current = searchString;
        lastSyncedFilters.current = defaultFilters;
        navigate(newUrl, {
          state: location.state,
          replace: true,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // URL → Filters (back/forward button)
  useEffect(() => {
    if (isInitialRender.current) {
      return;
    }

    const currentSearch = new URLSearchParams(location.search).toString();
    if (currentSearch === lastSyncedUrlSearch.current) {
      return;
    }

    const newFilters = parseFiltersFromUrl(location.search, defaultFilters);
    lastSyncedUrlSearch.current = currentSearch;
    lastSyncedFilters.current = newFilters;
    setFilters(newFilters);
  }, [location.search, defaultFilters]);

  // Filters → URL (user changes filters)
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    if (JSON.stringify(filters) === JSON.stringify(lastSyncedFilters.current)) {
      return;
    }

    const params = buildSearchParams(filters);
    const searchString = params.toString();
    const newUrl = `${location.pathname}${searchString ? `?${searchString}` : ''}`;

    lastSyncedUrlSearch.current = searchString;
    lastSyncedFilters.current = filters;
    navigate(newUrl, { state: location.state });
  }, [filters, navigate, location.pathname, location.state]);

  return [filters, setFilters];
};
